import { z } from 'zod';

import {
  ANALYTICS,
  CLIENT_EVENTS,
  METRIC_KEYS,
  type ClientEventName,
  type MetricKey,
} from '../constants/analytics';
import { IdSchema } from './common';

/**
 * Контракт панели метрик и клиентского трекинга (BLUEPRINT 1.4, раздел 6, Stage 13).
 * Пять метрик раздела 1.4 + воронка онбординга + недельные ряды + выгрузка CSV.
 */

// ───────────────────────────── трекинг с клиента (задача 1) ─────────────────────────────

export const ClientEventNameSchema = z.enum(
  [...CLIENT_EVENTS] as [ClientEventName, ...ClientEventName[]],
);

/** Свойства клиентского события: плоский объект, значения — строки, числа, флаги. */
export const ClientEventPropsSchema = z
  .record(z.union([z.string().max(200), z.number(), z.boolean(), z.null()]))
  .refine((p) => Object.keys(p).length <= 10, { message: 'Слишком много свойств события' });

export const AnalyticsTrackInputSchema = z.object({
  name: ClientEventNameSchema,
  props: ClientEventPropsSchema.optional(),
});
export type AnalyticsTrackInput = z.infer<typeof AnalyticsTrackInputSchema>;

// ───────────────────────────── фильтр панели (задача 3) ─────────────────────────────

export const MetricsFilterSchema = z
  .object({
    cityId: IdSchema.optional(),
    /** Глубина периода в днях; недельные ряды всё равно ограничены `ANALYTICS.weeks` */
    days: z.number().int().min(7).max(ANALYTICS.maxPeriodDays).default(ANALYTICS.defaultPeriodDays),
  })
  .default({ days: ANALYTICS.defaultPeriodDays });
export type MetricsFilter = z.infer<typeof MetricsFilterSchema>;

export const MetricKeySchema = z.enum(METRIC_KEYS as [MetricKey, ...MetricKey[]]);
export const MetricStatusSchema = z.enum(['good', 'warn', 'bad', 'unknown']);
export type MetricStatusValue = z.infer<typeof MetricStatusSchema>;

/** Карточка метрики: значение, порог, цвет и человекочитаемый знаменатель. */
export const MetricCardSchema = z.object({
  key: MetricKeySchema,
  /** Значение метрики: проценты или копейки (см. `unit`) */
  value: z.number().nullable(),
  /** Значение за предыдущий такой же период — для метрик «растёт»/«падает» */
  previous: z.number().nullable(),
  target: z.number().nullable(),
  unit: z.enum(['pct', 'money']),
  direction: z.enum(['up', 'down']),
  status: MetricStatusSchema,
  /** Числитель и знаменатель: «14 из 20» под карточкой */
  part: z.number().nullable(),
  total: z.number().nullable(),
});
export type MetricCard = z.infer<typeof MetricCardSchema>;

// ───────────────────────────── ряды по неделям (задача 2) ─────────────────────────────

export const MetricWeekPointSchema = z.object({
  /** Понедельник недели, `YYYY-MM-DD` по таймзоне города */
  week: z.string(),
  /** Средняя явка на сбор, % состава */
  attendancePct: z.number().nullable(),
  /** Сборов проведено за неделю */
  gatheringsDone: z.number().int().nonnegative(),
  /** Отрядов собрано за неделю */
  squadsFormed: z.number().int().nonnegative(),
  /** Оплачено заказов за неделю */
  ordersPaid: z.number().int().nonnegative(),
  /** Выручка за неделю, копейки */
  revenueKopecks: z.number().int().nonnegative(),
  /** Стоимость часа площадки на участника, копейки */
  venueCostKopecks: z.number().nullable(),
});
export type MetricWeekPoint = z.infer<typeof MetricWeekPointSchema>;

// ───────────────────────────── воронка онбординга (задача 2) ─────────────────────────────

export const FunnelStepSchema = z.object({
  key: z.string(),
  count: z.number().int().nonnegative(),
  /**
   * Доля от первого шага с данными, %. Не превышает 100: шаги считаются по одной когорте —
   * людям с первого непустого шага (Stage 16, раунд 1).
   */
  ofFirstPct: z.number().int().min(0).max(100),
  /** Доля от предыдущего шага, % */
  ofPrevPct: z.number().int().min(0).max(100),
});
export type FunnelStep = z.infer<typeof FunnelStepSchema>;

// ───────────────────────────── дожитие отрядов (задача 2) ─────────────────────────────

export const SurvivalPointSchema = z.object({
  /** Номер сбора в сезоне, 1..6 */
  index: z.number().int().positive(),
  /** Отрядов, дошедших до этого сбора */
  squads: z.number().int().nonnegative(),
  /** Доля от отрядов, дошедших до первого сбора, % */
  sharePct: z.number().int().min(0).max(100),
});
export type SurvivalPoint = z.infer<typeof SurvivalPointSchema>;

// ───────────────────────────── источники мест (задача 2) ─────────────────────────────

export const SeatSourceRowSchema = z.object({
  source: z.string(),
  count: z.number().int().nonnegative(),
  sharePct: z.number().int().min(0).max(100),
});
export type SeatSourceRow = z.infer<typeof SeatSourceRowSchema>;

// ───────────────────────────── время в пуле по ячейкам (задача 2) ─────────────────────────────

export const CellWaitRowSchema = z.object({
  cellKey: z.string(),
  activityName: z.string(),
  squadSize: z.number().int().positive(),
  weekday: z.number().int().min(1).max(7),
  levelBand: z.number().int(),
  count: z.number().int().nonnegative(),
  medianWaitDays: z.number().nonnegative(),
  maxWaitDays: z.number().nonnegative(),
});
export type CellWaitRow = z.infer<typeof CellWaitRowSchema>;

// ───────────────────────────── юнит-экономика города (Stage 14, задача 5) ─────────────────────────────

/**
 * Экономика одного отряда за период: выручка по оплаченным заказам минус стоимость площадок
 * по прайсу (`Venue.pricePerHour × duration`). Цена площадки — ориентир прайса, а не факт
 * оплаты: договоры с площадками система не хранит.
 */
export const UnitEconomicsRowSchema = z.object({
  squadId: IdSchema,
  squadLabel: z.string(),
  activityName: z.string(),
  capacity: z.number().int().positive(),
  /** Сборов проведено за период */
  gatheringsDone: z.number().int().nonnegative(),
  /** Оплачено участниками за период, копейки */
  revenueKopecks: z.number().int(),
  /** Стоимость площадок за проведённые сборы, копейки */
  venueCostKopecks: z.number().int(),
  /** Выручка − площадки, копейки */
  marginKopecks: z.number().int(),
  /** Маржа к выручке, %; null — выручки за период не было */
  marginPct: z.number().int().nullable(),
});
export type UnitEconomicsRow = z.infer<typeof UnitEconomicsRowSchema>;

export const UnitEconomicsSchema = z.object({
  cityId: IdSchema.nullable(),
  cityName: z.string().nullable(),
  from: z.string(),
  to: z.string(),
  revenueKopecks: z.number().int(),
  venueCostKopecks: z.number().int(),
  marginKopecks: z.number().int(),
  marginPct: z.number().int().nullable(),
  /** Маржа на отряд в среднем, копейки */
  marginPerSquadKopecks: z.number().int().nullable(),
  squads: z.array(UnitEconomicsRowSchema),
});
export type UnitEconomics = z.infer<typeof UnitEconomicsSchema>;

// ───────────────────────────── сводка панели ─────────────────────────────

export const MetricsOverviewSchema = z.object({
  cityId: IdSchema.nullable(),
  cityName: z.string().nullable(),
  /** Границы периода, ISO */
  from: z.string(),
  to: z.string(),
  cards: z.array(MetricCardSchema),
  weeks: z.array(MetricWeekPointSchema),
  funnel: z.array(FunnelStepSchema),
  survival: z.array(SurvivalPointSchema),
  seatSources: z.array(SeatSourceRowSchema),
  cells: z.array(CellWaitRowSchema),
  /** Событий в `Event` за период — видно, что трекинг живой */
  events: z.number().int().nonnegative(),
});
export type MetricsOverview = z.infer<typeof MetricsOverviewSchema>;

// ───────────────────────────── выгрузка CSV (задача 3) ─────────────────────────────

export const METRICS_EXPORTS = ['weeks', 'squads', 'gatherings', 'orders', 'cells'] as const;
export type MetricsExportKind = (typeof METRICS_EXPORTS)[number];

export const MetricsExportInputSchema = z.object({
  kind: z.enum(METRICS_EXPORTS),
  cityId: IdSchema.optional(),
  days: z.number().int().min(7).max(ANALYTICS.maxPeriodDays).default(ANALYTICS.defaultPeriodDays),
});
export type MetricsExportInput = z.infer<typeof MetricsExportInputSchema>;

export const MetricsExportSchema = z.object({
  /** Имя файла без расширения */
  filename: z.string(),
  /** Готовый CSV с разделителем `;` */
  csv: z.string(),
  rows: z.number().int().nonnegative(),
});
export type MetricsExport = z.infer<typeof MetricsExportSchema>;

// ───────────────────────────── еженедельный отчёт владельцу (задача 4) ─────────────────────────────

export const MetricsAnomalySchema = z.object({
  kind: z.enum(['lowAttendance', 'staleCell', 'refunds']),
  text: z.string(),
});
export type MetricsAnomaly = z.infer<typeof MetricsAnomalySchema>;

export const OwnerReportSchema = z.object({
  cityId: IdSchema,
  cityName: z.string(),
  week: z.string(),
  cards: z.array(MetricCardSchema),
  anomalies: z.array(MetricsAnomalySchema),
});
export type OwnerReport = z.infer<typeof OwnerReportSchema>;
